// === PERSONENAUTO — form validation ===
// Pure validation extracted from PersonenAutoScreen. validatePersonenauto returns a map of i18n
// keys (the screen translates them); computeKwWarning returns the kW-vs-driver-age block's i18n
// key (or null); scrollToFirstError focuses the first errored field. `now` is injected so the
// age-dependent rules are deterministic in tests. Mirrors rechtsbijstand/validation.ts.

import { requiresPurchaseDateForAkw } from '../../../utils/autoGarantie';

type ValidationErrors = Record<string, string>;

// Form-field element ids (to scroll to the first error), keyed by validation error key.
export const AUTO_ERROR_FIELD_IDS: Record<string, string> = {
    '_addressValidation': 'address-postcode',
    '_kwValidation': 'kw-warning',
    'BA_PCODE': 'address-postcode',
    'BA_HUISNR': 'address-huisnr',
    'OB_KENTEKE': 'kenteken',
    'OB_AFMKM': 'afmkm',
    'OB_AANKDAT': 'aankdat',
    'OB_EERSTEI': 'ob-eerstei',
    'OB_TNOKLAS': 'ob-tnoklas',
    'PP_SCHDVRY': 'schdvry',
    'BS_RELSRTC': 'bs-relsrtc',
    'BS_VOORL': 'bs-voorl',
    'BS_ANAAM': 'bs-anaam',
    'BS_GESLACH': 'bs-gender',
    'BS_GEBDAT': 'bs-date-of-birth',
};

export function validatePersonenauto(data: any, now: Date = new Date()): ValidationErrors {
    const errors: Record<string, string> = {};
    const ba = data?.Contractdocument?.PP?.BA;
    const ob = data?.Contractdocument?.PP?.OB;
    const pp = data?.Contractdocument?.PP;

    const V = 'screens.personenauto.validation.';

    // Address must be validated
    if (!ba?.BA_PCODE) {
      errors['BA_PCODE'] = `${V}postalCodeRequired`;
    }
    if (!ba?.BA_HUISNR) {
      errors['BA_HUISNR'] = `${V}houseNumberRequired`;
    }
    if ((ba?.BA_PCODE && ba?.BA_HUISNR) && (!ba?.BA_STRAAT || !ba?.BA_PLAATS)) {
      errors['_addressValidation'] = `${V}addressInvalid`;
    }

    // Kenteken (OB_MERK is filled async by the kenteken lookup, so we only
    // hard-require OB_KENTEKE here — lookup failures surface via kentekenLookupError)
    if (!ob?.OB_KENTEKE) errors['OB_KENTEKE'] = `${V}licensePlateRequired`;

    // Jaarkilometrage
    if (ob?.OB_AFMKM === undefined || ob?.OB_AFMKM === null) errors['OB_AFMKM'] = `${V}annualMileageRequired`;

    // Eerste eigenaar — required once kenteken is loaded so NW/AKW can be priced upfront.
    if (ob?.OB_MERK && !ob?.OB_EERSTEI) errors['OB_EERSTEI'] = `${V}firstOwnerRequired`;

    // CCV Beveiligingsklasse — required once the vehicle is loaded (pre-filled from the kenteken lookup).
    if (ob?.OB_MERK && !ob?.OB_TNOKLAS) errors['OB_TNOKLAS'] = `${V}securityClassRequired`;

    // Schadevrije jaren
    if (pp?.PP_SCHDVRY === undefined || pp?.PP_SCHDVRY === null) errors['PP_SCHDVRY'] = `${V}noClaimsYearsRequired`;

    const hoofdV = data?.Supportsection?.Dekkingen?.Hoofddekking || 'WA';
    if (hoofdV === 'WA+volledig' && ob?.OB_EERSTEI === 'N') {
      if (requiresPurchaseDateForAkw(ob, now)) {
        if (!ob?.OB_AANKDAT) {
          errors['OB_AANKDAT'] = `${V}purchaseDateRequired`;
        } else {
          const aankRaw = String(ob.OB_AANKDAT);
          const dattoelRaw = String(ob?.OB_DATTOEL || '');
          if (aankRaw.length === 8 && dattoelRaw.length === 8) {
            const aankNum = parseInt(aankRaw, 10);
            const dattoelNum = parseInt(dattoelRaw, 10);
            if (aankNum < dattoelNum) {
              errors['OB_AANKDAT'] = `${V}purchaseDateBeforeRegistration`;
            }
          }
        }
      }
    }

    // BS fields required when driver is different from policyholder
    if ((data?.Supportsection?.Divers?.Hoofdbestuurder ?? 'J') === 'N') {
      const bs = pp?.BS;
      if (!bs?.BS_RELSRTC || bs.BS_RELSRTC === 'A') errors['BS_RELSRTC'] = `${V}driverRelationRequired`;
      if (!bs?.BS_VOORL) errors['BS_VOORL'] = `${V}initialsRequired`;
      if (!bs?.BS_ANAAM) errors['BS_ANAAM'] = `${V}lastNameRequired`;
      if (!bs?.BS_GESLACH) errors['BS_GESLACH'] = `${V}genderRequired`;
      if (!bs?.BS_GEBDAT) errors['BS_GEBDAT'] = `${V}dateOfBirthRequired`;
    }

    return errors;
}

export function computeKwWarning(data: any, relationData: any, now: Date = new Date()): string | null {
    const ob = data?.Contractdocument?.PP?.OB;
    const aantkw = Number(ob?.OB_AANTKW || 0);
    if (aantkw <= 0) return null;

    let gebdatStr: string | null = null;
    const isPolicyholderDriver = (data?.Supportsection?.Divers?.Hoofdbestuurder ?? 'J') === 'J';
    if (isPolicyholderDriver && relationData?.clientInfo?.dateOfBirth) {
      const dob = String(relationData.clientInfo.dateOfBirth).replace(/-/g, '');
      if (dob.length === 8) {
        gebdatStr = dob.substring(4, 8) + dob.substring(2, 4) + dob.substring(0, 2);
      }
    }
    if (!gebdatStr) {
      const bsGebdat = data?.Contractdocument?.PP?.BS?.BS_GEBDAT;
      if (bsGebdat) gebdatStr = String(bsGebdat);
    }
    if (!gebdatStr || gebdatStr.length !== 8) return null;

    const birthYear = parseInt(gebdatStr.substring(0, 4), 10);
    const birthMonth = parseInt(gebdatStr.substring(4, 6), 10) - 1;
    const birthDay = parseInt(gebdatStr.substring(6, 8), 10);
    let driverAge = now.getFullYear() - birthYear;
    if (now.getMonth() < birthMonth || (now.getMonth() === birthMonth && now.getDate() < birthDay)) {
      driverAge--;
    }
    const merk = (ob?.OB_MERK || '').toUpperCase();
    const isTesla = merk.includes('TESLA');

    if (driverAge < 28 && aantkw > 150) {
      return 'screens.personenauto.kwWarning.under28';
    } else if (driverAge >= 28 && driverAge <= 70 && aantkw > 300 && !isTesla) {
      return 'screens.personenauto.kwWarning.mid';
    } else if (driverAge > 70 && aantkw > 150) {
      return 'screens.personenauto.kwWarning.over70';
    }
    return null;
}

export function scrollToFirstError(errors: ValidationErrors): void {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;
    for (const errorKey of errorKeys) {
      const elementId = AUTO_ERROR_FIELD_IDS[errorKey];
      if (elementId) {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return;
        }
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
}
